import {
    Box,
    deriveColumnWidth as componentsDeriveColumnWidth,
    LinkText,
    Table,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";

import style from "./template-table.scss";
import Star, { Color as StarColor } from "components/icons/Star";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import flexibleModalStyle from "components/modal/flexible-modal.scss";
import FlexibleModal from "components/modal/FlexibleModal";
import Modal from "components/modal/Modal";
import { ReportTemplateTableKebabMenu } from "components/reports/kebab-menu/ReportTemplateTableKebabMenu";
import ReportTemplateForm from "components/reports/report-template/ReportTemplateForm";
import ReportTemplateView from "components/reports/report-template/ReportTemplateView";
import DateCell from "components/table/DateCell";
import {
    BLANCCO_DEFAULT_REPORT_TEMPLATE_UUID,
    reportSections,
    ReportTemplate,
    ReportType,
    TemplateTableData,
} from "domain/reports";
import { reportTemplateService } from "services/report/erasure/ReportTemplateService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import buttons from "styles/buttons.scss";
import layoutStyle from "styles/layout.scss";
import { RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

interface TableState {
    templateData: TemplateTableData[];
}

interface Result {
    title: string;
    message: string;
    resultVisible: boolean;
}

interface ModalState {
    uuid: string;
    templateName: string;
    modalVisibility: boolean;
}

export interface Props {
    allLoaded: boolean;
    count: number;
    setTemplatesFetched: (fetched: boolean) => void;
    onTemplatesFetched: (doFetch: TemplateTableData[]) => void;
    onTemplateDelete: () => void;
    onTemplateUpdate: () => void;
    onTemplateSetDefault: () => void;
}
const ReportTemplatesTable = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [tableState, setTableState] = React.useState<TableState>({ templateData: [] });
    const [reportTemplate, setReportTemplate] = React.useState<ReportTemplate | undefined | null>(null);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const [dynamicSize, setDynamicSize] = React.useState(true);
    const [templateVisibility, setTemplateVisibility] = React.useState<ModalState>({
        uuid: "",
        templateName: "",
        modalVisibility: false,
    });
    const manageDynamicSize = (initial: boolean) => {
        setDynamicSize(initial);
    };

    const [refreshCount, setRefreshCount] = React.useState(0);
    const [editTemplateModalVisible, setEditTemplateModalVisible] = React.useState(false);
    const [editTemplateModalLoading, setEditTemplateModalLoading] = React.useState(false);
    const [result, setResult] = React.useState<Result>({
        title: "",
        message: "",
        resultVisible: false,
    });
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const defaultTemplate: TemplateTableData = {
        uuid: BLANCCO_DEFAULT_REPORT_TEMPLATE_UUID,
        templateName: t("ErasureReport.default"),
        reportType: ReportType.STANDARD,
        logoVisible: false,
        disclaimer: "",
        sections: reportSections.filter((each) => each !== "ERASURE_VERIFICATION"),
        createdDate: "2020-01-01T00:00:00Z",
        editedDate: "",
        createdBy: "Blancco Management Portal",
        editedBy: "",
        defaultReportTemplate: {
            own: false,
            other: {
                count: 0,
                usernames: [],
            },
        },
    };

    const onToggleFullscreen = () => {
        setRefreshCount(refreshCount + 1);
    };

    const fetchData = (abortController: AbortController) => {
        setInitialLoading(true);
        reportTemplateService
            .fetchTemplates(abortController)
            .then((data) => {
                const ownDefaultExists =
                    data.find((template) => template.defaultReportTemplate.own === true) !== undefined;
                defaultTemplate.defaultReportTemplate.own = !ownDefaultExists;
                data.unshift(defaultTemplate);
                setTableState((prevState) => ({
                    ...prevState,
                    templateData: data,
                }));
                props.onTemplatesFetched(data);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("TemplatesTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                }
                props.setTemplatesFetched(true);
            });
    };

    const onShowModal = (show: boolean) => {
        setEditTemplateModalVisible(show);
    };

    const onShowEditModal = (uuid: string) => {
        setDynamicSize(false);
        onShowModal(true);
        setReportTemplate(tableState.templateData.find((template) => template.uuid === uuid));
    };

    const onViewTemplateEdit = (uuid: string) => {
        hideShowTemplateModal();
        setDynamicSize(false);
        onShowModal(true);
        setReportTemplate(tableState.templateData.find((template) => template.uuid === uuid));
    };

    const showTemplateModal = (uuid: string, templateName: string) => {
        setTemplateVisibility({
            uuid,
            templateName,
            modalVisibility: true,
        });
    };

    const hideShowTemplateModal = () => {
        setDynamicSize(true);
        setTemplateVisibility({
            uuid: "",
            templateName: "",
            modalVisibility: false,
        });
        usageStatisticsService.sendEvent({
            category: Category.REPORT_TEMPLATE,
            action: Action.VIEW_REPORT_TEMPLATE,
        });
    };

    const onTemplateEditSubmitHandler = (template: ReportTemplate) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setEditTemplateModalLoading(true);
        setDynamicSize(true);
        reportTemplateService
            .updateReportTemplate(template, abortController)
            .then(() => {
                setEditTemplateModalVisible(false);
                setDynamicSize(true);
                setResult({
                    title: t("EditTemplate.templateUpdated.success.title"),
                    message: t("EditTemplate.templateUpdated.success.message", { name: template.templateName }),
                    resultVisible: true,
                });
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    let errorMessage;
                    try {
                        errorMessage =
                            JSON.parse(error.message).errorCode == "CONFLICT"
                                ? t("EditTemplate.templateUpdated.failed.versionMismatchedMessage")
                                : t("EditTemplate.templateUpdated.failed.message");
                    } catch {
                        errorMessage = t("EditTemplate.templateUpdated.failed.permissionChangedMessage");
                    }
                    setEditTemplateModalVisible(false);
                    setDynamicSize(true);
                    setResult({
                        title: t("EditTemplate.templateUpdated.failed.title"),
                        message: errorMessage,
                        resultVisible: true,
                    });
                }
            });
    };

    const hideResultAndRedirectToRoot = () => {
        setResult((prevState) => ({ ...prevState, resultVisible: false }));
        setEditTemplateModalLoading(false);
        props.onTemplateUpdate();
    };

    const columns: Array<Column<TemplateTableData>> = [
        {
            Header: () => <TextCell key="name">{t("TemplatesTable.name")}</TextCell>,
            accessor: "templateName",
            Cell: (cellInfo) => {
                return (
                    <Box gap={"Xs"}>
                        {!hasTenantCookie() && (
                            <ReportTemplateTableKebabMenu
                                uuid={cellInfo.cell.row.original.uuid}
                                name={cellInfo.cell.row.original.templateName}
                                onTemplateDelete={props.onTemplateDelete}
                                onTemplateEdit={onShowEditModal}
                                onTemplateSetDefault={props.onTemplateSetDefault}
                                tableRef={tableContainerRef}
                            />
                        )}
                        <LinkText
                            as={"button"}
                            onClick={() =>
                                showTemplateModal(
                                    cellInfo.cell.row.original.uuid,
                                    cellInfo.cell.row.original.templateName
                                )
                            }
                        >
                            {cellInfo.value}
                        </LinkText>
                    </Box>
                );
            },
        },
        {
            Header: () => <TextCell key="reportType">{t("Common.uuid")}</TextCell>,
            accessor: "uuid",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: componentsDeriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextCell key="reportType">{t("TemplatesTable.reportType")}</TextCell>,
            accessor: "reportType",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: componentsDeriveColumnWidth(12, tableContainerRef),
        },
        {
            Header: () => <TextCell key="createdDate">{t("TemplatesTable.createdDate")}</TextCell>,
            accessor: "createdDate",
            Cell: ({ cell: { value } }) => <DateCell value={value} />,
            width: componentsDeriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextCell key="createdBy">{t("TemplatesTable.createdBy")}</TextCell>,
            accessor: "createdBy",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: componentsDeriveColumnWidth(18, tableContainerRef),
        },
        {
            Header: () => <TextCell key="editedDate">{t("TemplatesTable.editedDate")}</TextCell>,
            accessor: "editedDate",
            Cell: ({ cell: { value } }) =>
                value ? <DateCell value={value} /> : <TextCell>{t("TemplatesTable.notEdited")}</TextCell>,
            width: componentsDeriveColumnWidth(14, tableContainerRef),
        },
        {
            Header: () => <TextCell key="editedBy">{t("TemplatesTable.editedBy")}</TextCell>,
            accessor: "editedBy",
            Cell: ({ cell: { value } }) => <TextCell>{value || t("TemplatesTable.notEdited")}</TextCell>,
            width: componentsDeriveColumnWidth(20, tableContainerRef),
        },
        {
            Header: () => <TextCell key="defaultReportTemplate.own">{t("TemplatesTable.default")}</TextCell>,
            accessor: "defaultReportTemplate",
            id: "defaultReportTemplate.own",
            Cell: ({ cell: { value } }) =>
                value.own === true && (
                    <Tooltip placement={"top"}>
                        <TooltipContent testId={testIds.common.primaryView.table.cellTooltip.itself}>
                            {t("TemplatesTable.defaultTooltip")}
                        </TooltipContent>
                        <TooltipTrigger testId={testIds.common.primaryView.table.cellTooltip.trigger}>
                            <Star color={StarColor.GREEN} titleText={""} />
                        </TooltipTrigger>
                    </Tooltip>
                ),
            width: componentsDeriveColumnWidth(10, tableContainerRef),
        },
        {
            Header: () => <TextCell key="defaultTemplate.other">{t("TemplatesTable.otherDefaults")}</TextCell>,
            accessor: "defaultReportTemplate",
            id: "defaultReportTemplate.other",
            Cell: ({ cell: { value } }) => {
                const usernames = value.other.usernames;
                const hasMore = usernames.length < value.other.count;
                return (
                    usernames.length > 0 && (
                        <Tooltip placement={"top"}>
                            <TooltipContent testId={testIds.common.primaryView.table.cellTooltip.itself}>
                                <Box columnLayout>
                                    <Box>{t("TemplatesTable.otherDefaultsTooltip")}</Box>
                                    {usernames.map((username, index) => {
                                        const isLast = index === usernames.length - 1;

                                        return (
                                            <Box key={username}>{isLast && !hasMore ? username : username + ","}</Box>
                                        );
                                    })}
                                    {hasMore && <Box>{"..."}</Box>}
                                </Box>
                            </TooltipContent>
                            <TooltipTrigger testId={testIds.common.primaryView.table.cellTooltip.trigger}>
                                <Star color={StarColor.GRAY} titleText={""} />
                            </TooltipTrigger>
                        </Tooltip>
                    )
                );
            },
            width: componentsDeriveColumnWidth(12, tableContainerRef),
        },
    ];
    React.useEffect(() => {
        const abortController = new AbortController();
        fetchData(abortController);

        return () => {
            abortController.abort();
        };
    }, [props.count]);

    React.useEffect(() => {
        templateVisibility.modalVisibility && setDynamicSize(true);
    }, [templateVisibility.modalVisibility]);

    let dataCount = null;
    if (tableState.templateData.length > 0 && props.allLoaded) {
        dataCount = t("Common.defaultSearchResultHint", { dataCount: tableState.templateData.length });
    }

    return (
        <>
            <Box paddingBottom={"Xs"}>{dataCount}</Box>
            <Table
                className={layoutStyle.tableContainer}
                containerRef={tableContainerRef}
                tableIdentity={RepositoryKey.TEMPLATES_TABLE}
                data={tableState.templateData}
                columns={columns}
                loaded={!initialLoading && props.allLoaded}
                failureMessage={requestFailureMessage}
                tooltips={true}
                emptyStateMessage={t("TemplatesTable.emptyStateMessage")}
                testIds={{ itself: testIds.workArea.report.erasure.reportTemplate.table.itself }}
            />
            <FlexibleModal
                key="edit"
                isOpen={editTemplateModalVisible}
                hideModal={() => setEditTemplateModalVisible(false)}
                title={t("EditTemplate.title")}
                onToggleFullscreen={onToggleFullscreen}
                dynamicSize={dynamicSize}
                fullscreenIcon={true}
                className={flexibleModalStyle.reportTemplateVariant}
            >
                {editTemplateModalLoading ? (
                    <LoadingIndicator />
                ) : (
                    <ReportTemplateForm
                        templates={tableState.templateData}
                        submitEventHandler={onTemplateEditSubmitHandler}
                        onShowModal={onShowModal}
                        template={reportTemplate}
                        hasDynamicSize={manageDynamicSize}
                    />
                )}
            </FlexibleModal>
            <Modal isOpen={result.resultVisible} hideModal={hideResultAndRedirectToRoot} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResultAndRedirectToRoot}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            <FlexibleModal
                key="view"
                isOpen={templateVisibility.modalVisibility}
                hideModal={hideShowTemplateModal}
                title={t("viewTemplate.title", { templateName: templateVisibility.templateName })}
                onToggleFullscreen={onToggleFullscreen}
                dynamicSize={dynamicSize}
                fullscreenIcon={true}
                className={flexibleModalStyle.reportTemplateVariant}
            >
                <ReportTemplateView
                    uuid={templateVisibility.uuid}
                    onCancel={hideShowTemplateModal}
                    onEdit={onViewTemplateEdit}
                    defaultTemplate={defaultTemplate}
                    hasDynamicSize={manageDynamicSize}
                />
            </FlexibleModal>
        </>
    );
};

export default ReportTemplatesTable;
